import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { 
  Bell, 
  BellOff, 
  Star, 
  Shield, 
  Award,
  AlertTriangle,
  Users,
  Loader2,
  CheckCircle,
  XCircle
} from "lucide-react";

const NotificationSettings = ({ onClose }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState("default");
  const [preferences, setPreferences] = useState({
    new_rating: true,
    level_change: true,
    new_badge: true,
    safe_check_alert: true,
    new_referral: true
  });
  const [vapidKey, setVapidKey] = useState(null);

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/push/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data);
    } catch (error) {
      console.error("Error fetching preferences:", error);
    }
  }, [token]);

  useEffect(() => {
    checkPermission();
    checkSubscription();
    fetchPreferences();
    fetchVapidKey();
  }, [fetchPreferences]);

  const fetchVapidKey = async () => {
    try {
      const response = await axios.get(`${API}/push/vapid-public-key`);
      setVapidKey(response.data.publicKey);
    } catch (error) {
      console.error("Error fetching VAPID key:", error);
    }
  };

  const checkPermission = () => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  };

  const checkSubscription = async () => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    }
  };

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      toast.error("Votre navigateur ne supporte pas les notifications");
      return false;
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    return result === "granted";
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      // Request permission first
      const hasPermission = await requestPermission();
      if (!hasPermission) {
        toast.error("Permission refusée pour les notifications");
        setLoading(false);
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push
      if (!vapidKey) {
        toast.error("Clé de notification non disponible");
        setLoading(false);
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsSubscribed(true);
      toast.success("Notifications activées !");
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error("Erreur lors de l'activation des notifications");
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        // Remove from backend
        await axios.delete(`${API}/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setIsSubscribed(false);
      toast.success("Notifications désactivées");
    } catch (error) {
      console.error("Unsubscribe error:", error);
      toast.error("Erreur lors de la désactivation");
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    try {
      await axios.put(`${API}/push/preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Error updating preferences:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const notificationTypes = [
    { 
      key: "new_rating", 
      label: "Nouvelles évaluations", 
      description: "Quand quelqu'un vous évalue",
      icon: Star 
    },
    { 
      key: "level_change", 
      label: "Changement de niveau", 
      description: "Passage Citoyen → Gardien → Légende",
      icon: Shield 
    },
    { 
      key: "new_badge", 
      label: "Nouveaux badges", 
      description: "Quand vous débloquez un badge",
      icon: Award 
    },
    { 
      key: "safe_check_alert", 
      label: "Alertes Safe Check", 
      description: "SOS et temps dépassé",
      icon: AlertTriangle 
    },
    { 
      key: "new_referral", 
      label: "Nouveaux filleuls", 
      description: "Quand quelqu'un s'inscrit avec votre code",
      icon: Users 
    }
  ];

  return (
    <Card className="guardian-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell size={20} className="text-[#FF3366]" />
          Notifications Push
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Permission Status */}
        {permission === "denied" && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle size={20} />
              <span className="font-medium">Notifications bloquées</span>
            </div>
            <p className="text-sm text-red-600 mt-1">
              Allez dans les paramètres de votre navigateur pour autoriser les notifications.
            </p>
          </div>
        )}

        {/* Main Toggle */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#FF3366]/5 to-pink-50 rounded-xl border border-[#FF3366]/20">
          <div className="flex items-center gap-3">
            {isSubscribed ? (
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Bell className="text-green-600" size={20} />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <BellOff className="text-slate-400" size={20} />
              </div>
            )}
            <div>
              <p className="font-semibold text-slate-900">
                {isSubscribed ? "Notifications actives" : "Notifications désactivées"}
              </p>
              <p className="text-sm text-slate-500">
                {isSubscribed ? "Vous recevrez des alertes en temps réel" : "Activez pour ne rien manquer"}
              </p>
            </div>
          </div>
          <Button
            onClick={isSubscribed ? unsubscribe : subscribe}
            disabled={loading || permission === "denied"}
            className={isSubscribed 
              ? "bg-slate-200 text-slate-700 hover:bg-slate-300" 
              : "btn-guardian"
            }
          >
            {loading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : isSubscribed ? (
              "Désactiver"
            ) : (
              "Activer"
            )}
          </Button>
        </div>

        {/* Notification Type Preferences */}
        {isSubscribed && (
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900">Types de notifications</h3>
            {notificationTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div 
                  key={type.key}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} className="text-[#FF3366]" />
                    <div>
                      <p className="font-medium text-slate-900">{type.label}</p>
                      <p className="text-xs text-slate-500">{type.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences[type.key]}
                    onCheckedChange={(checked) => updatePreference(type.key, checked)}
                    className="data-[state=checked]:bg-[#FF3366]"
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Info */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-700">
            💡 Les notifications fonctionnent même quand l'app est fermée. 
            Parfait pour ne pas manquer une alerte Safe Check !
          </p>
        </div>

        {onClose && (
          <Button variant="outline" className="w-full rounded-xl" onClick={onClose}>
            Fermer
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
